// Client-side hint that mirrors the server router rules so we can show a
// "Pay with X · powered by Y" caption under pay buttons. Server routing
// remains authoritative — this is presentational only.
import { useTranslation } from "react-i18next";
import { useCountry } from "@/hooks/useCountry";

export type HintProvider = "stripe" | "stripe-br" | "paystack" | "flutterwave" | "flutterwave-mpesa" | "wave";

export const WAVE_COUNTRIES = new Set(["SN", "CI"]);

const PAYSTACK = new Set(["NG", "GH", "ZA", "KE"]);
const WAVE = new Set(["SN", "CI"]);
const FLW_MPESA = new Set(["KE"]); // KE is on Paystack, but kept for completeness
const FLW = new Set([
  "UG", "RW", "TZ", "ZM", "CM", "CG", "CD", "ML", "BF", "BJ", "TG",
  "MW", "ET", "EG", "MA", "TN", "NE", "GA", "MZ", "MG",
]);

function pickHintProvider(country: string | null | undefined): HintProvider | null {
  const cc = (country ?? "").toUpperCase();
  if (!cc) return null;
  if (cc === "BR") return "stripe-br";
  if (PAYSTACK.has(cc)) return "paystack";
  if (WAVE.has(cc)) return "wave";
  if (cc === "TZ") return "flutterwave";
  if (FLW_MPESA.has(cc)) return "flutterwave-mpesa";
  if (FLW.has(cc)) return "flutterwave";
  return null; // default Stripe EUR — no caption shown
}

export function useProviderHint(): {
  provider: HintProvider | null;
  label: string | null;
  fallbackCta: string | null;
  isWaveCountry: boolean;
} {
  const { t } = useTranslation();
  const { country } = useCountry();
  const provider = pickHintProvider(country);
  const isWaveCountry = WAVE_COUNTRIES.has((country ?? "").toUpperCase());
  if (!provider) return { provider: null, label: null, fallbackCta: null, isWaveCountry: false };
  const key = `marketing.payments.poweredBy.${
    provider === "stripe-br" ? "stripeBr" :
    provider === "flutterwave-mpesa" ? "flutterwaveMpesa" :
    provider
  }`;
  const fallback = {
    "stripe-br": "Pay with Pix or card · powered by Stripe",
    "paystack": "Pay with card or bank transfer · powered by Paystack",
    "wave": "Pay with Wave or card · powered by Wave",
    "flutterwave": "Pay with mobile money or card · powered by Flutterwave",
    "flutterwave-mpesa": "Pay with M-Pesa or card · powered by Flutterwave",
    "stripe": "",
  }[provider];
  const fallbackCta = isWaveCountry
    ? t("payments.wave.fallbackCta", { defaultValue: "Prefer card or mobile money? Use Flutterwave" })
    : null;
  return { provider, label: t(key, { defaultValue: fallback }), fallbackCta, isWaveCountry };
}
